import React, { useEffect, useMemo, useState } from 'react';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { Home, LogOut, GitCompare, Link as LinkIcon, Info, ChevronDown, FileText, Shield, Mail, UserRoundCog, Cog, Hammer, Menu, Search, X } from 'lucide-react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { boardsAPI } from '../services/api';
import { assetPath } from '../utils/appPaths';

const BRAND_LOGO_WIDTH = 480;
const BRAND_LOGO_HEIGHT = 200;

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  z-index: 1000;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;

const HeaderContent = styled.div`
  width: min(var(--site-max-width), calc(100% - (var(--site-gutter) * 2)));
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 24px;
  height: var(--header-height);
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: #1e293b;
  font-size: 24px;
  font-weight: 700;
  
  &:hover {
    color: #2c9fa4;
  }
`;

const LogoImage = styled.img.attrs({
  width: BRAND_LOGO_WIDTH,
  height: BRAND_LOGO_HEIGHT,
  decoding: 'async',
  fetchPriority: 'high',
})`
  height: var(--header-logo-height);
  width: auto;
`;

const HeaderSearchSlot = styled.div`
  position: relative;
  flex: 1 1 280px;
  max-width: 380px;
  min-width: 160px;
  display: ${props => props.$mobile ? 'none' : 'block'};

  @media (max-width: 1180px) {
    max-width: 300px;
  }

  @media (max-width: 900px) {
    display: ${props => props.$mobile ? 'block' : 'none'};
    width: 100%;
    min-width: 0;
    max-width: none;
  }
`;

const HeaderSearchForm = styled.form`
  width: 100%;
`;

const HeaderSearchField = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const HeaderSearchIcon = styled(Search)`
  position: absolute;
  left: 14px;
  color: #94a3b8;
  pointer-events: none;
`;

const HeaderSearchInput = styled.input`
  width: 100%;
  padding: 11px 14px 11px 42px;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  background: #f8fafc;
  color: #1e293b;
  font-size: 14px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #2c9fa4;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.15);
    background: white;
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const HeaderSearchResults = styled.div`
  position: ${props => props.$mobile ? 'static' : 'absolute'};
  top: ${props => props.$mobile ? 'auto' : 'calc(100% + 8px)'};
  left: 0;
  right: 0;
  margin-top: ${props => props.$mobile ? '8px' : '0'};
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.16);
  overflow: hidden;
  z-index: 1002;
`;

const HeaderSearchStatus = styled.div`
  padding: 12px 14px;
  color: #64748b;
  font-size: 13px;
`;

const HeaderSearchResultButton = styled.button`
  width: 100%;
  display: grid;
  gap: 4px;
  padding: 12px 14px;
  border: none;
  border-top: 1px solid #f1f5f9;
  background: white;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:first-child {
    border-top: none;
  }

  &:hover {
    background: #f8fafc;
  }
`;

const HeaderSearchResultTitle = styled.span`
  color: #1e293b;
  font-size: 14px;
  font-weight: 600;
`;

const HeaderSearchResultMeta = styled.span`
  color: #64748b;
  font-size: 12px;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: clamp(12px, 1.6vw, 32px);
  margin-left: auto;

  @media (max-width: 900px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: ${props => props.$active ? '#2c9fa4' : '#64748b'};
  font-weight: ${props => props.$active ? '600' : '500'};
  font-size: 16px;
  font-family: inherit;
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    color: #2c9fa4;
    background-color: #f1f5f9;
  }
`;

const BetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  padding: 3px 7px;
  border-radius: 999px;
  background: #fef3c7;
  color: #92400e;
  font-size: 0.68rem;
  font-weight: 900;
  line-height: 1;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  vertical-align: middle;
`;

const NavBetaBadge = styled(BetaBadge)`
  margin-left: 6px;
  padding: 2px 6px;
  font-size: 0.58rem;
`;

const UserInfo = styled.span`
  font-size: 14px;
  opacity: 0.9;
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  color: ${props => props.$active ? '#2c9fa4' : '#64748b'};
  font-weight: ${props => props.$active ? '600' : '500'};
  font-size: 16px;
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 0.2s ease;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  
  &:hover {
    color: #2c9fa4;
    background-color: #f1f5f9;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  min-width: 200px;
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
`;

const DropdownItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  text-decoration: none;
  color: #374151;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #f1f5f9;
    color: #2c9fa4;
  }
  
  &:first-child {
    border-radius: 8px 8px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 8px 8px;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  background: none;
  border: none;
  color: #1e293b;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;

  &:hover {
    background-color: #f1f5f9;
    color: #2c9fa4;
  }

  @media (max-width: 900px) {
    display: flex;
  }
`;

const MobileNav = styled.nav`
  display: none;

  @media (max-width: 900px) {
    display: ${props => props.$isOpen ? 'flex' : 'none'};
    flex-direction: column;
    gap: 4px;
    position: absolute;
    top: var(--header-height);
    left: 0;
    right: 0;
    background: white;
    border-bottom: 1px solid #e2e8f0;
    box-shadow: 0 8px 16px rgba(15, 23, 42, 0.12);
    padding: 12px var(--site-gutter) 16px;
  }
`;

const MobileSearchSection = styled.div`
  margin-bottom: 8px;
`;

const MobileNavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: ${props => props.$active ? '#2c9fa4' : '#334155'};
  font-weight: ${props => props.$active ? '600' : '500'};
  font-size: 16px;
  padding: 12px 10px;
  border-radius: 8px;

  &:hover {
    color: #2c9fa4;
    background-color: #f1f5f9;
  }
`;

const MobileMenuDivider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin: 6px 0;
`;

const MobileLogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  color: #334155;
  font-weight: 500;
  font-size: 16px;
  font-family: inherit;
  padding: 12px 10px;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    color: #ef4444;
    background-color: #fef2f2;
  }
`;

const normalizeBoardsPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.pinouts)) return payload.pinouts;
  if (Array.isArray(payload?.boards)) return payload.boards;
  if (Array.isArray(payload?.data)) return payload.data;

  return [];
};

const getBoardDisplayName = (board) => (
  board?.name || board?.title || board?.chip_name || 'Unnamed Board'
);

const getBoardDetailUrl = (board) => `/board/${encodeURIComponent(board?.slug || board?.id || '')}`;

const getBoardSearchText = (board) => ([
  board?.name,
  board?.title,
  board?.chip_name,
  board?.manufacturer,
  board?.category,
  board?.package_type,
  board?.description,
]
  .filter(Boolean)
  .join(' ')
  .toLowerCase());

const isBoardDetailPath = (pathname) => (
  Boolean(matchPath('/board/:id', pathname) || matchPath('/boards/:id', pathname))
);

const isBuilderPath = (pathname) => (
  pathname === '/builder' || pathname === '/project-builder'
);

const isWiringPath = (pathname) => (
  pathname === '/WiringSuggest' ||
  pathname === '/wiring-guides' ||
  pathname.startsWith('/wiring-guide/')
);

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [boardSearch, setBoardSearch] = useState('');
  const [availableBoards, setAvailableBoards] = useState([]);
  const [isBoardSearchLoading, setIsBoardSearchLoading] = useState(false);
  const [hasLoadedBoardSearchOptions, setHasLoadedBoardSearchOptions] = useState(false);
  const [hasBoardSearchError, setHasBoardSearchError] = useState(false);
  const [isBoardSearchOpen, setIsBoardSearchOpen] = useState(false);
  const showBoardSearch = useMemo(() => isBoardDetailPath(location.pathname), [location.pathname]);
  const currentBoardRouteId = useMemo(
    () => decodeURIComponent(location.pathname.split('/').filter(Boolean).pop() || ''),
    [location.pathname]
  );
  const currentBoard = useMemo(
    () => availableBoards.find((board) => {
      const boardIdentifier = String(board?.slug || board?.id || '');
      const boardId = String(board?.id || '');
      const boardSlug = String(board?.slug || '');

      return (
        boardIdentifier === currentBoardRouteId
        || boardId === currentBoardRouteId
        || boardSlug === currentBoardRouteId
      );
    }) || null,
    [availableBoards, currentBoardRouteId]
  );

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsBoardSearchOpen(false);
    setBoardSearch('');
  }, [location.pathname]);

  useEffect(() => {
    if (!showBoardSearch || hasLoadedBoardSearchOptions || isBoardSearchLoading) {
      return;
    }

    let isMounted = true;

    const loadBoards = async () => {
      setIsBoardSearchLoading(true);
      setHasBoardSearchError(false);

      try {
        const response = await boardsAPI.getAll({ include_svg: 0 });
        if (!isMounted) {
          return;
        }

        console.log('Board search raw response:', response.data);
        console.log('Board search normalized:', normalizeBoardsPayload(response.data));

        setAvailableBoards(normalizeBoardsPayload(response.data));
      } catch (error) {
        console.error('Failed to load board search options:', error);
        if (isMounted) {
          setHasBoardSearchError(true);
        }
      } finally {
        if (isMounted) {
          setIsBoardSearchLoading(false);
          setHasLoadedBoardSearchOptions(true);
        }
      }
    };

    loadBoards();

    return () => {
      isMounted = false;
    };
  }, [hasLoadedBoardSearchOptions, isBoardSearchLoading, showBoardSearch]);

  useEffect(() => {
    if (!showBoardSearch) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('[data-board-search-root="1"]')) {
        setIsBoardSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [showBoardSearch]);

  const filteredBoards = useMemo(() => {
    const normalizedQuery = boardSearch.trim().toLowerCase();

    const otherBoards = availableBoards.filter((board) => {
      const boardIdentifier = String(board?.slug || board?.id || '');
      const boardId = String(board?.id || '');
      const boardSlug = String(board?.slug || '');

      return !(
        boardIdentifier === currentBoardRouteId
        || boardId === currentBoardRouteId
        || boardSlug === currentBoardRouteId
      );
    });

    if (!normalizedQuery) {
      return otherBoards.slice(0, 6);
    }

    return otherBoards
      .map((board) => {
        const searchText = getBoardSearchText(board);
        const displayName = getBoardDisplayName(board).toLowerCase();
        const manufacturer = String(board?.manufacturer || '').toLowerCase();
        let score = 0;

        if (displayName === normalizedQuery) {
          score += 400;
        } else if (displayName.startsWith(normalizedQuery)) {
          score += 220;
        } else if (displayName.includes(normalizedQuery)) {
          score += 140;
        }

        if (manufacturer.startsWith(normalizedQuery)) {
          score += 70;
        } else if (manufacturer.includes(normalizedQuery)) {
          score += 40;
        }

        if (searchText.includes(normalizedQuery)) {
          score += 20;
        }

        return {
          board,
          score,
        };
      })
      .filter((entry) => entry.score > 0)
      .sort((left, right) => {
        if (right.score !== left.score) {
          return right.score - left.score;
        }

        return getBoardDisplayName(left.board).localeCompare(getBoardDisplayName(right.board), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      })
      .slice(0, 6)
      .map((entry) => entry.board);
  }, [availableBoards, boardSearch, currentBoardRouteId]);
  const currentBoardMatchesQuery = useMemo(() => {
    const normalizedQuery = boardSearch.trim().toLowerCase();
    if (!normalizedQuery || !currentBoard) {
      return false;
    }

    return getBoardSearchText(currentBoard).includes(normalizedQuery);
  }, [boardSearch, currentBoard]);

  useEffect(() => {
    console.log({
      boardSearch,
      availableBoardsCount: availableBoards.length,
      filteredBoardsCount: filteredBoards.length,
      isBoardSearchOpen,
      showBoardSearch,
    });
  }, [
    boardSearch,
    availableBoards.length,
    filteredBoards.length,
    isBoardSearchOpen,
    showBoardSearch,
  ]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navigateToBoard = (board) => {
    const nextBoardUrl = getBoardDetailUrl(board);
    if (!nextBoardUrl || nextBoardUrl === '/board/') {
      return;
    }

    setBoardSearch('');
    setIsBoardSearchOpen(false);
    setIsMobileMenuOpen(false);
    navigate(nextBoardUrl);
  };

  const handleBoardSearchSubmit = (event) => {
    event.preventDefault();

    if (filteredBoards.length > 0) {
      navigateToBoard(filteredBoards[0]);
    }
  };

  const renderBoardSearch = (isMobile = false) => {
    if (!showBoardSearch) {
      return null;
    }

    const shouldShowResults = isBoardSearchOpen && (
      isBoardSearchLoading
      || hasBoardSearchError
      || filteredBoards.length > 0
      || boardSearch.trim().length > 0
      || availableBoards.length > 0
    );

    return (
      <HeaderSearchSlot $mobile={isMobile} data-board-search-root="1">
        <HeaderSearchForm onSubmit={handleBoardSearchSubmit}>
          <HeaderSearchField>
            <HeaderSearchIcon size={16} />
            <HeaderSearchInput
              type="search"
              value={boardSearch}
              placeholder="Search another board..."
              onChange={(event) => {
                const nextValue = event.target.value;
                setBoardSearch(nextValue);
                setIsBoardSearchOpen(true);
              }}
              onFocus={() => setIsBoardSearchOpen(true)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setIsBoardSearchOpen(false);
                  event.currentTarget.blur();
                }
              }}
              aria-label="Search another board"
              autoComplete="off"
            />
          </HeaderSearchField>
        </HeaderSearchForm>

        {shouldShowResults && (
          <HeaderSearchResults $mobile={isMobile}>
            {isBoardSearchLoading && (
              <HeaderSearchStatus>Loading boards...</HeaderSearchStatus>
            )}

            {!isBoardSearchLoading && hasBoardSearchError && (
              <HeaderSearchStatus>Board search is temporarily unavailable.</HeaderSearchStatus>
            )}

            {!isBoardSearchLoading && !hasBoardSearchError && filteredBoards.length === 0 && boardSearch.trim() && !currentBoardMatchesQuery && (
              <HeaderSearchStatus>No matching boards found.</HeaderSearchStatus>
            )}

            {!isBoardSearchLoading && !hasBoardSearchError && filteredBoards.length === 0 && !boardSearch.trim() && (
              <HeaderSearchStatus>Start typing or pick from the published boards below.</HeaderSearchStatus>
            )}

            {!isBoardSearchLoading && !hasBoardSearchError && currentBoardMatchesQuery && (
              <HeaderSearchStatus>
                You&apos;re already viewing {getBoardDisplayName(currentBoard)}.
              </HeaderSearchStatus>
            )}

            {!isBoardSearchLoading && !hasBoardSearchError && filteredBoards.map((board) => (
              <HeaderSearchResultButton
                key={board.id || board.slug}
                type="button"
                onClick={() => navigateToBoard(board)}
              >
                <HeaderSearchResultTitle>
                  {getBoardDisplayName(board)}
                </HeaderSearchResultTitle>
                <HeaderSearchResultMeta>
                  {[board.manufacturer, board.package_type].filter(Boolean).join(' | ') || 'Board'}
                </HeaderSearchResultMeta>
              </HeaderSearchResultButton>
            ))}
          </HeaderSearchResults>
        )}
      </HeaderSearchSlot>
    );
  };
  
  return (
    <HeaderContainer>
      <HeaderContent>
        <Logo to="/">
          <LogoImage src={assetPath('/logo.png')} alt="MicroPinouts Logo" />
        </Logo>

        {renderBoardSearch()}

        <MobileMenuButton
          type="button"
          onClick={() => setIsMobileMenuOpen(prev => !prev)}
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </MobileMenuButton>

        <Nav>
          <NavLink to="/" $active={location.pathname === '/'}>
            <Home size={20} />
            Home
          </NavLink>

          <NavLink to="/compare" $active={location.pathname === '/compare'}>
            <GitCompare size={20} />
            Compare
          </NavLink>

          <NavLink to="/builder" $active={isBuilderPath(location.pathname)}>
            <Hammer size={20} />
            Builder
            <NavBetaBadge>Beta</NavBetaBadge>
          </NavLink>

          <NavLink to="/guides" $active={location.pathname.startsWith('/guides')}>
            <FileText size={20} />
            Guides
          </NavLink>

          <NavLink
            to="/WiringSuggest"
            $active={isWiringPath(location.pathname)}
          >
            <LinkIcon size={20} />
            Wiring
          </NavLink>

          <DropdownContainer>
            <DropdownButton
              $active={
                location.pathname === '/about' ||
                location.pathname === '/contact' ||
                location.pathname === '/legal' ||
                location.pathname === '/privacy'
              }
              onClick={() => navigate('/about')}
              onMouseEnter={() => setIsAboutOpen(true)}
              onMouseLeave={() => setIsAboutOpen(false)}
            >
              <Info size={20} />
              About
              <ChevronDown size={16} />
            </DropdownButton>

            <DropdownMenu
              $isOpen={isAboutOpen}
              onMouseEnter={() => setIsAboutOpen(true)}
              onMouseLeave={() => setIsAboutOpen(false)}
            >
              <DropdownItem to="/about">
                <Info size={16} />
                About MicroPinouts
              </DropdownItem>

              <DropdownItem to="/contact">
                <Mail size={16} />
                Contact
              </DropdownItem>

              <DropdownItem to="/legal">
                <FileText size={16} />
                Legal and Licensing
              </DropdownItem>

              <DropdownItem to="/privacy">
                <Shield size={16} />
                Privacy Policy
              </DropdownItem>
            </DropdownMenu>
          </DropdownContainer>

          {isAuthenticated && (
            <DropdownContainer>
              <DropdownButton
                $active={location.pathname.startsWith('/admin')}
                onClick={() => setIsAccountOpen(!isAccountOpen)}
                onMouseEnter={() => setIsAccountOpen(true)}
                onMouseLeave={() => setIsAccountOpen(false)}
              >
                <UserRoundCog size={20} />
                <UserInfo>{user?.username}</UserInfo>
                <ChevronDown size={16} />
              </DropdownButton>

              <DropdownMenu
                $isOpen={isAccountOpen}
                onMouseEnter={() => setIsAccountOpen(true)}
                onMouseLeave={() => setIsAccountOpen(false)}
              >
                <DropdownItem to="/admin">
                  <Cog size={16} />
                  Manage
                </DropdownItem>

                <DropdownButton
                  type="button"
                  onClick={handleLogout}
                >
                  <LogOut size={16} />
                  Logout
                </DropdownButton>
              </DropdownMenu>
            </DropdownContainer>
          )}
        </Nav>
      </HeaderContent>

      <MobileNav $isOpen={isMobileMenuOpen}>
        {showBoardSearch && (
          <MobileSearchSection>
            {renderBoardSearch(true)}
          </MobileSearchSection>
        )}

        <MobileNavLink
          to="/"
          $active={location.pathname === '/'}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <Home size={20} />
          Home
        </MobileNavLink>

        <MobileNavLink
          to="/compare"
          $active={location.pathname === '/compare'}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <GitCompare size={20} />
          Compare
        </MobileNavLink>

        <MobileNavLink
          to="/builder"
          $active={isBuilderPath(location.pathname)}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <Hammer size={20} />
          Builder
        </MobileNavLink>

        <MobileNavLink
          to="/guides"
          $active={location.pathname.startsWith('/guides')}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <FileText size={20} />
          Guides
        </MobileNavLink>

        <MobileNavLink
          to="/WiringSuggest"
          $active={isWiringPath(location.pathname)}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <LinkIcon size={20} />
          Wiring
        </MobileNavLink>

        <MobileMenuDivider />

        <MobileNavLink
          to="/about"
          $active={location.pathname === '/about'}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <Info size={20} />
          About MicroPinouts
        </MobileNavLink>

        <MobileNavLink
          to="/contact"
          $active={location.pathname === '/contact'}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <Mail size={20} />
          Contact
        </MobileNavLink>

        <MobileNavLink
          to="/legal"
          $active={location.pathname === '/legal'}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <FileText size={20} />
          Legal and Licensing
        </MobileNavLink>

        <MobileNavLink
          to="/privacy"
          $active={location.pathname === '/privacy'}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <Shield size={20} />
          Privacy Policy
        </MobileNavLink>

        {isAuthenticated && (
          <>
            <MobileMenuDivider />

            <MobileNavLink
              to="/admin"
              $active={location.pathname.startsWith('/admin')}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Cog size={20} />
              Manage
            </MobileNavLink>

            <MobileLogoutButton
              type="button"
              onClick={() => {
                setIsMobileMenuOpen(false);
                handleLogout();
              }}
            >
              <LogOut size={20} />
              Logout
            </MobileLogoutButton>
          </>
        )}
      </MobileNav>
    </HeaderContainer>
  );
};

export default Header;
